import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import CyberCubLogo from '../../components/CyberCubLogo.jsx';
import Tutor from '../../components/Tutor.jsx';
import { useFinished } from '../../lib/practical.js';
import { WEB_LABS } from './webLabData.js';

/**
 * One lab engine for the whole web-hacking track (Modules 3-14).
 *
 * Every practical in that track is the same safe shape: read a short
 * scenario, then answer a few questions about how the thing works and
 * how you would stop it. Nothing here attacks anything real - there is
 * no live target, no payload and no tool. It is understanding and
 * recognition, which is the course's whole stance, done from data so
 * twelve practicals share one tested component.
 *
 * A lab is finished when every judging stage is fully correct. The
 * completion flag is keyed on the live path, which is exactly the
 * href the lesson page links to, so the mandatory-practical gate picks
 * it up with no per-lab wiring.
 */
export default function WebLab() {
  const { id } = useParams();
  const cfg = WEB_LABS[id];

  const storeKey = `cybercub.lab.web.${id}`;
  const load = () => {
    try { return JSON.parse(localStorage.getItem(storeKey) ?? '{}'); } catch { return {}; }
  };
  const saved = useMemo(load, [storeKey]);

  const [tab, setTab] = useState(0);
  const [picks, setPicks] = useState(saved.picks ?? {});

  useEffect(() => {
    if (cfg) document.title = `${cfg.title} · CyberCub Lab`;
  }, [cfg]);
  useEffect(() => {
    try { localStorage.setItem(storeKey, JSON.stringify({ picks })); } catch { /* private window */ }
  }, [storeKey, picks]);

  const stages = cfg?.stages ?? [];
  const stageDone = (st) => st.items.every((it) => picks[it.id] === it.answer);
  const allDone = stages.length > 0 && stages.every(stageDone);

  useFinished(cfg ? `/lab/web/${id}` : null, allDone);

  if (!cfg) {
    return (
      <div className="lab">
        <p>That practical could not be found.</p>
        <Link to="/learn" className="btn btn-sm btn-ghost">← Back to my courses</Link>
      </div>
    );
  }

  // A stage unlocks once the one before it is finished.
  const unlockedUpto = stages.reduce((n, st, i) => (i === 0 || stageDone(stages[i - 1]) ? i : n), 0);
  const active = stages[Math.min(tab, stages.length - 1)];

  return (
    <div className="lab">
      <header className="lab-top">
        <div className="row gap-sm">
          <CyberCubLogo size={38} />
          <div>
            <strong>{cfg.title}</strong>
            <span className="lab-sub">Practical · {cfg.moduleLabel}</span>
          </div>
        </div>
        <Link to={`/learn/lesson/${cfg.lessonSlug}`} className="btn btn-sm btn-ghost">
          ← Back to the lesson
        </Link>
      </header>

      <nav className="lab-tabs" aria-label="Sections">
        {stages.map((st, i) => {
          const locked = i > unlockedUpto;
          return (
            <button
              key={st.id}
              type="button"
              className={`lab-tab ${i === tab ? 'is-active' : ''} ${locked ? 'is-locked' : ''}`}
              onClick={() => !locked && setTab(i)}
              disabled={locked}
              aria-current={i === tab ? 'page' : undefined}
            >
              <span className="lab-tab-n">{i + 1}</span>
              {st.label}
              {locked && <span aria-label="locked"> 🔒</span>}
            </button>
          );
        })}
      </nav>

      <section className="lab-section">
        <div className="place-head">
          <div>
            <h2>{active.heading}</h2>
            <p className="lab-lead">{active.lead}</p>
          </div>
          <span className="pill">
            {active.items.filter((it) => picks[it.id] === it.answer).length} / {active.items.length}
          </span>
        </div>

        <Tutor avatar={active.avatar ?? 'cub-teal'} say={stageDone(active) ? active.tutorDone : active.tutorIdle} />

        {active.scenario && (
          <div className="weblab-scenario">
            {active.scenario.map((line, i) => (
              <p key={i} className={line.startsWith('>') ? 'weblab-code' : ''}>
                {line.replace(/^>\s?/, '')}
              </p>
            ))}
          </div>
        )}

        {active.items.map((it) => {
          const pick = picks[it.id];
          const answered = pick !== undefined;
          const right = pick === it.answer;
          return (
            <div key={it.id} className={`lab-case ${answered ? (right ? 'is-right' : 'is-wrong') : ''}`}>
              <span className="lab-case-n" aria-hidden="true">{it.icon ?? '🔎'}</span>
              <div className="grow">
                <strong>{it.q}</strong>
                <div className="lab-choices" style={{ marginTop: '0.5rem' }}>
                  {it.options.map((opt, oi) => (
                    <button
                      key={opt}
                      type="button"
                      className={`lab-choice ${pick === oi ? 'is-picked' : ''}`}
                      onClick={() => setPicks((p) => ({ ...p, [it.id]: oi }))}
                      disabled={right}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {answered && (
                  <p className={`lab-feedback ${right ? 'is-right' : 'is-wrong'}`}>
                    {right ? `✓ ${it.why}` : '✗ Not quite — read the scenario again and think about what is really being trusted.'}
                  </p>
                )}
              </div>
            </div>
          );
        })}

        {stageDone(active) && tab < stages.length - 1 && (
          <div className="lab-done">
            <div>
              <strong>{active.doneNote ?? 'Stage complete.'}</strong>
            </div>
            <button type="button" className="btn btn-sm" onClick={() => setTab(tab + 1)}>
              Next stage →
            </button>
          </div>
        )}

        {allDone && tab === stages.length - 1 && (
          <div className="lab-done">
            <div>
              <strong>{cfg.outro}</strong>
            </div>
            <Link to={`/learn/lesson/${cfg.lessonSlug}`} className="btn btn-sm">
              ← Back to the lesson
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
